import React from 'react';
import { Flame } from 'lucide-react';

interface StreakDisplayProps {
  streakValue: number;
  color: 'green' | 'red' | 'yellow' | 'neutral';
  lastCompletedWeek?: string;
  weekStatus?: 'green' | 'red' | 'baseline' | 'missing';
  onClick?: () => void;
  tooltip?: string;
}

/**
 * StreakDisplay - Consecutive green weeks indicator
 *
 * Streak Logic (Column 2):
 * - Counts consecutive GREEN weeks (weight loss OR stable)
 * - Resets on RED week (weight gain) or MISSING week (no data)
 * - Stable weight (0 change) = GREEN (maintains streak)
 *
 * Color Coding:
 * - GREEN: Active streak building
 * - YELLOW: Current week in progress
 * - NEUTRAL: No streak (0 weeks)
 * - RED: Streak broken (just reset)
 */
export const StreakDisplay: React.FC<StreakDisplayProps> = ({
  streakValue,
  color,
  lastCompletedWeek: _lastCompletedWeek,
  weekStatus: _weekStatus,
  onClick,
  tooltip
}) => {
  const getColorStyles = () => {
    switch (color) {
      case 'green':
        return {
          bg: '#22c55e',      // Vibrant green
          text: '#ffffff',    // White text
          border: '#16a34a',  // Darker green border
          icon: '#ffffff'     // White icon
        };
      case 'yellow':
        return {
          bg: '#fbbf24',      // Vibrant amber
          text: '#78350f',    // Dark brown text
          border: '#f59e0b',  // Darker amber border
          icon: '#78350f'     // Dark brown icon
        };
      case 'neutral':
        return {
          bg: '#64748b',      // Slate gray
          text: '#ffffff',    // White text
          border: '#475569',  // Darker slate border
          icon: '#ffffff'     // White icon
        };
      case 'red':
        return {
          bg: '#ef4444',      // Vibrant red
          text: '#ffffff',    // White text
          border: '#dc2626',  // Darker red border
          icon: '#ffffff'     // White icon
        };
      default:
        return {
          bg: '#64748b',
          text: '#ffffff',
          border: '#475569',
          icon: '#ffffff'
        };
    }
  };

  const getTooltipText = () => {
    if (streakValue === 0) {
      return 'No active streak - Starting fresh!';
    } else if (streakValue === 1) {
      return '1 consecutive green week';
    } else {
      return `${streakValue} consecutive green weeks`;
    }
  };

  const colors = getColorStyles();

  return (
    <>
      <div
        className={`streak-display ${onClick ? 'clickable' : ''}`}
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          borderColor: colors.border
        }}
        title={tooltip || getTooltipText()}
        onClick={onClick}
      >
        <div className="streak-icon" style={{ color: colors.icon }}>
          {streakValue > 0 ? (
            <Flame size={16} className={streakValue >= 4 ? 'flame-animated' : ''} />
          ) : (
            <span className="streak-emoji">🔄</span>
          )}
        </div>
        <div className="streak-content">
          <div className="streak-value">
            {streakValue}
            <span className="streak-label">week{streakValue !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      <style>{`
        .streak-display {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid;
          font-size: 14px;
          min-width: 90px;
          transition: all 0.2s ease;
        }

        .streak-display.clickable {
          cursor: pointer;
        }

        .streak-display.clickable:hover {
          opacity: 0.9;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
        }

        .streak-display:not(.clickable):hover {
          opacity: 0.95;
        }

        .streak-icon {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .streak-emoji {
          font-size: 16px;
          line-height: 1;
        }

        .flame-animated {
          animation: flicker 1.5s ease-in-out infinite;
        }

        @keyframes flicker {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }

        .streak-content {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 1;
        }

        .streak-value {
          display: flex;
          align-items: baseline;
          gap: 4px;
          font-weight: 700;
          font-size: 16px;
          line-height: 1;
        }

        .streak-label {
          font-size: 11px;
          font-weight: 500;
          opacity: 0.8;
        }
      `}</style>
    </>
  );
};
